// Configuration for route-specific metadata
import { getSurveyName } from './surveyMapping';

// Specific survey metadata configurations
const surveySpecificMetadata = {
  '622b2280-16ae-11f0-94ea-9574364caebe': {
    title: "Enterprises Readiness Assessment Survey",
    description: "Assess your enterprise's readiness for Security Operations Center implementation. Evaluate current security posture and identify gaps for SOC maturity improvement."
  },
  '7cb2a510-151e-11f0-998d-f7cbc9c2d788': {
    title: "Startup AI Readiness Assessment Survey",
    description: "Comprehensive cybersecurity framework assessment for SOC implementation. Analyze your organization's security controls and readiness for SOC deployment."
  },
  'b6420220-5ba7-11ef-b484-6dfb5d512f67': {
    title: "Security Operations Center (SOC) - Maturity Assessment",
    description: "Comprehensive cybersecurity framework assessment for SOC implementation. Analyze your organization's security controls and readiness for SOC deployment."
  }
  // Add more survey-specific metadata here as needed
};

export const routeMetadata = {
  // Default metadata
  default: {
    title: "Security Operations Center (SOC) - Maturity Assessment",
    description: "Security Operations Center (SOC) - Assess your SOC's Maturity level and safeguard your digital assets."
  },
  
  // Survey landing page
  surveyLanding: (surveyId, surveyName = null) => {
    // Check if we have specific metadata for this survey ID
    if (surveySpecificMetadata[surveyId]) {
      return surveySpecificMetadata[surveyId];
    }
    
    // Fallback to generic metadata
    const surveyIdentifier = surveyName || getSurveyName(surveyId) || `Survey ${surveyId.substring(0, 8)}`;
    return {
      title: `${surveySpecificMetadata[surveyId].title}`,
      description: `Begin your Security Operations Center maturity assessment for ${surveyIdentifier}. Evaluate your SOC's capabilities and identify areas for improvement.`
    };
  },
  
  // Q&A section
  qna: (surveyId, surveyName = null) => {
    // Check if we have specific metadata for this survey ID
    if (surveySpecificMetadata[surveyId]) {
      return {
        title: `${surveySpecificMetadata[surveyId].title} - Questions`,
        description: `Answer comprehensive questions for ${surveySpecificMetadata[surveyId].title}. Assess your organization's security maturity across various domains.`
      };
    }
    
    // Fallback to generic metadata
    const surveyIdentifier = surveyName || getSurveyName(surveyId) || `Survey ${surveyId.substring(0, 8)}`;
    return {
      title: `Assessment Questions`,
      description: `Answer comprehensive questions to assess your Security Operations Center maturity level for ${surveyIdentifier} across various security domains.`
    };
  },
  
  // Thank you page
  thankYou: (surveyId, surveyName = null) => {
    // Check if we have specific metadata for this survey ID
    if (surveySpecificMetadata[surveyId]) {
      return {
        title: `Thank You - ${surveySpecificMetadata[surveyId].title}`,
        description: `Thank you for completing the ${surveySpecificMetadata[surveyId].title}. Your assessment results are being processed and analyzed.`
      };
    }
    
    // Fallback to generic metadata
    const surveyIdentifier = surveyName || getSurveyName(surveyId) || `Survey ${surveyId.substring(0, 8)}`;
    return {
      title: `Thank You - Assessment Complete`,
      description: `Thank you for completing your Security Operations Center maturity assessment for ${surveyIdentifier}. Your results are being processed.`
    };
  },
  
  // Report preview
  reportPreview: (surveyId, surveyName = null) => {
    // Check if we have specific metadata for this survey ID
    if (surveySpecificMetadata[surveyId]) {
      return {
        title: `${surveySpecificMetadata[surveyId].title} - Report Preview`,
        description: `Preview your ${surveySpecificMetadata[surveyId].title} report with detailed analysis and recommendations.`
      };
    }
    
    // Fallback to generic metadata
    const surveyIdentifier = surveyName || getSurveyName(surveyId) || `Survey ${surveyId.substring(0, 8)}`;
    return {
      title: `SOC Assessment Report Preview`,
      description: `Preview your Security Operations Center maturity assessment report for ${surveyIdentifier} and detailed analysis.`
    };
  },
  
  // Final report
  report: (surveyId, surveyName = null) => {
    // Check if we have specific metadata for this survey ID
    if (surveySpecificMetadata[surveyId]) {
      return {
        title: `${surveySpecificMetadata[surveyId].title} - Final Report`,
        description: `Your comprehensive ${surveySpecificMetadata[surveyId].title} report with detailed analysis, recommendations, and actionable insights.`
      };
    }
    
    // Fallback to generic metadata
    const surveyIdentifier = surveyName || getSurveyName(surveyId) || `Survey ${surveyId.substring(0, 8)}`;
    return {
      title: `SOC Maturity Assessment Report`,
      description: `Your comprehensive Security Operations Center maturity assessment report for ${surveyIdentifier} with detailed analysis and recommendations.`
    };
  },
  
  // Not found page
  notFound: {
    title: "Page Not Found",
    description: "The requested page could not be found. Please check the URL or return to the assessment homepage."
  }
};

// Function to get metadata based on current path
export const getMetadataForPath = (pathname) => {
  // Remove leading slash and get the first segment
  const path = pathname.replace(/^\//, '');
  
  if (!path || path === '') {
    return routeMetadata.notFound;
  }
  
  // Extract survey ID from different route patterns
  let surveyId = null;
  
  // Check for specific route patterns
  if (path.match(/^[a-zA-Z0-9-]+$/)) {
    // Single segment (survey_id) - landing page
    surveyId = path;
    return routeMetadata.surveyLanding(surveyId);
  }
  
  if (path.startsWith('qna/')) {
    // Extract survey ID from qna/survey_id/ss_id
    const pathParts = path.split('/');
    if (pathParts.length >= 2) {
      surveyId = pathParts[1];
      return routeMetadata.qna(surveyId);
    }
  }
  
  if (path.startsWith('thank-you/')) {
    // Extract survey ID from thank-you/survey_id/ss_id
    const pathParts = path.split('/');
    if (pathParts.length >= 2) {
      surveyId = pathParts[1];
      return routeMetadata.thankYou(surveyId);
    }
  }
  
  if (path.startsWith('report-preview/')) {
    // Extract survey ID from report-preview/survey_id/ss_id/srid/...
    const pathParts = path.split('/');
    if (pathParts.length >= 2) {
      surveyId = pathParts[1];
      return routeMetadata.reportPreview(surveyId);
    }
  }
  
  if (path.startsWith('report/')) {
    // Extract survey ID from report/survey_id/ss_id/srid/...
    const pathParts = path.split('/');
    if (pathParts.length >= 2) {
      surveyId = pathParts[1];
      return routeMetadata.report(surveyId);
    }
  }
  
  if (path === 'not-found') {
    return routeMetadata.notFound;
  }
  
  // Default fallback
  return routeMetadata.default;
};

 